import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, Mail, ArrowRight, Eye, EyeOff, Monitor, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import usePageTitle from "@/hooks/usePageTitle";

const LOGO_ICON = "/logo-glotech-icon.png";

export default function LoginPage() {
  usePageTitle("Connexion");
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const from = location.state?.from?.pathname || "/app";

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Bienvenue, ${user.email}`);
      navigate(from, { replace: true });
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Email ou mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] flex" data-testid="login-page">
      {/* Left Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          {/* Logo */}
          <div className="text-center">
            <img src={LOGO_ICON} alt="GLOTECH" className="h-16 w-16 mx-auto mb-8 rounded-xl object-cover" />
            <h1 className="text-3xl font-bold text-white mb-2">
              Connexion
            </h1>
            <p className="text-[#94A3B8] text-sm leading-relaxed">
              Portail unique GLOTECH &mdash; vous serez redirige automatiquement vers votre espace selon votre role.
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#94A3B8] text-sm font-medium">
                Adresse email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#64748b]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@exemple.com"
                  className="pl-10 h-12 bg-[#151B2B] border-[#2A3441] text-white placeholder:text-[#64748b] focus:border-[#0099CC] focus:ring-1 focus:ring-[#0099CC] rounded-lg"
                  data-testid="input-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#94A3B8] text-sm font-medium">
                Mot de passe
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#64748b]" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-12 h-12 bg-[#151B2B] border-[#2A3441] text-white placeholder:text-[#64748b] focus:border-[#0099CC] focus:ring-1 focus:ring-[#0099CC] rounded-lg"
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-[#0099CC] transition-colors"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#0099CC] hover:bg-[#0088B5] text-white font-semibold rounded-lg btn-glow"
              data-testid="submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Connexion...
                </>
              ) : (
                <>
                  Se connecter
                  <ArrowRight className="ml-2 h-5 w-5" />
                </>
              )}
            </Button>
          </form>

          {/* Forgot Password Link */}
          <div className="text-center">
            <Link
              to="/mot-de-passe-oublie"
              className="text-[#64748b] hover:text-[#0099CC] transition-colors text-sm"
              data-testid="forgot-password-link"
            >
              Mot de passe oublie ?
            </Link>
          </div>

          {/* Registration Links - Two distinct paths */}
          <div className="space-y-3 pt-2">
            <p className="text-center text-[#64748b] text-xs uppercase tracking-wider">Pas encore de compte ?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link
                to="/inscription"
                className="flex items-center gap-2.5 px-4 py-3 rounded-lg border border-[#2A3441] hover:border-[#0099CC]/50 bg-[#151B2B]/50 hover:bg-[#151B2B] transition-all group"
                data-testid="register-agency-link"
              >
                <Monitor className="h-4 w-4 text-[#0099CC] shrink-0" />
                <div>
                  <p className="text-white text-sm font-medium group-hover:text-[#0099CC] transition-colors">GLOTECH OS</p>
                  <p className="text-[#64748b] text-xs">Creer une agence</p>
                </div>
              </Link>
              <Link
                to="/event"
                className="flex items-center gap-2.5 px-4 py-3 rounded-lg border border-[#2A3441] hover:border-[#F59E0B]/50 bg-[#151B2B]/50 hover:bg-[#151B2B] transition-all group"
                data-testid="register-event-center-link"
              >
                <CalendarDays className="h-4 w-4 text-[#F59E0B] shrink-0" />
                <div>
                  <p className="text-white text-sm font-medium group-hover:text-[#F59E0B] transition-colors">Event Center</p>
                  <p className="text-[#64748b] text-xs">Demander un acces</p>
                </div>
              </Link>
            </div>
          </div>

        </div>
      </div>

      {/* Right Panel - Branding */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-[#0B0F19] via-[#151B2B] to-[#0B0F19] items-center justify-center p-12 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #0099CC 1px, transparent 1px),
                             radial-gradient(circle at 75% 75%, #0099CC 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="relative z-10 text-center max-w-lg">
          <h2 className="text-5xl font-bold text-white mb-4 leading-tight">
            JE VEUX.<br />
            <span className="text-[#0099CC]">JE PEUX.</span><br />
            JE FAIS.
          </h2>
          <p className="text-[#94A3B8] text-lg mt-6">
            Gerez vos projets digitaux et vos evenements sur une seule plateforme.
          </p>
          
          {/* Two pillars */}
          <div className="grid grid-cols-2 gap-4 mt-10">
            <div className="bg-[#0B0F19]/60 border border-[#2A3441] rounded-xl p-5 text-left">
              <Monitor className="h-6 w-6 text-[#0099CC] mb-3" />
              <p className="text-white font-semibold text-sm mb-1">GLOTECH OS</p>
              <p className="text-[#64748b] text-xs leading-relaxed">CRM, devis, propositions, projets web et suivi client en temps reel.</p>
            </div>
            <div className="bg-[#0B0F19]/60 border border-[#2A3441] rounded-xl p-5 text-left">
              <CalendarDays className="h-6 w-6 text-[#F59E0B] mb-3" />
              <p className="text-white font-semibold text-sm mb-1">Event Center</p>
              <p className="text-[#64748b] text-xs leading-relaxed">Evenements, inscriptions, QR Codes, controle d'acces et statistiques.</p>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 mt-10">
            <div>
              <p className="text-3xl font-bold text-[#0099CC]">5+</p>
              <p className="text-[#64748b] text-sm">Sites Actifs</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#0099CC]">100%</p>
              <p className="text-[#64748b] text-sm">Client satisfait</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#0099CC]">24/7</p>
              <p className="text-[#64748b] text-sm">Support</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
